import type { IrReport } from './types';

export const G1_IR_REPORTS: IrReport[] = [
  {
    key: 'G1R1',
    group: 'G1',
    rCode: 'R1',
    pillar: 'Capital Readiness',
    stage: 'Critical Risk Stage',
    tone: 'red',
    profile: 'Capital Dreamer',
    tagline: 'Your vision is alive, but your readiness has yet to be built.',
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are at the starting line of your fundraising journey. This means you currently lack the core building blocks that investors expect before they will consider providing capital. At this stage, your company may still be operating on vision and early hustle rather than structured materials, validated numbers, or investor-ready proof points. This is not a weakness — it is simply a signal that your focus must be on foundations, not fundraising. Approaching investors too soon can actually harm future chances, as first impressions carry weight. The fact that you've completed this diagnostic shows that you are self-aware and ready to begin laying down the path to investor credibility. You are what we call a Capital Dreamer: ambitious, driven, and at the very beginning of the readiness curve. Your goal now is to transform that dream into an investable story.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'The responses you gave in the diagnostic reveal several key signals:' },
          {
            kind: 'bullets',
            items: [
              'No structured pitch materials yet — you may have ideas or bullet points, but no investor deck, one-pager, or valuation summary.',
              'Financial model is missing or highly informal — revenue projections, expense planning, or funding needs are not yet documented in a credible way.',
              'Investor expectations unclear — you may not be familiar with the language of pre-money vs post-money valuation, dilution, or SAFE notes.',
              "High dependence on funding — the business vision relies on external capital, but the groundwork that demonstrates fundability hasn't been built.",
            ],
          },
          {
            kind: 'paragraph',
            text: 'Put simply: you are still in the pre-investor story phase. This is entirely normal at the very early stage, but it means your energy must be directed inward toward preparation before reaching outward to investors.',
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Craft Your Founder Narrative',
                sub: [
                  'Define Why this problem? Why now? Why you?',
                  'Draft a 5-slide pitch outline: Problem → Solution → Market → Why Now → Team',
                  'Avoid over-polishing — at this stage, clarity matters more than design.',
                ],
              },
              {
                title: 'Build Financial Basics',
                sub: [
                  'Create a 12-month budget that captures revenues, expenses, and cash needs',
                  'Define how much you would need to raise and what milestones that money would fund',
                  'Learn 3 essential investor metrics: Runway (months of cash left), Burn Rate (how fast you spend cash each month), Valuation Basics.',
                ],
              },
              {
                title: 'Understand Investor Landscape',
                sub: [
                  'Research 5 comparable startups in your industry (what stage did they raise, how much, from which investors)',
                  "Map your company's stage to the appropriate funding sources (Friends & family, Angel investors, Pre-seed accelerators)",
                  "Set realistic expectations — most institutional VCs won't invest at this stage.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At this stage, your platform unlocks Lite tools to help you move from zero to structured. These are deliberately simplified — the goal is momentum, not perfection.',
          },
          {
            kind: 'bullets',
            items: [
              'Capital Readiness Kit — Lite',
              '5-slide Pitch Deck Outline',
              'Valuation Basics Explainer (pre-money vs post-money)',
              'Data Room Starter Checklist',
              'Founder Story Framework — a guided workbook to articulate your "why"',
              'Startup Budget Template (Excel) — a one-page financial starter tool',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At G1R1, you are not yet in a position to raise capital. Attempting to do so would be premature and could risk damaging relationships with future investors. Instead, this is your groundwork stage — a time for building clarity, discipline, and structure. Think of this as investing in your own readiness before asking others to invest in you.',
          },
          {
            kind: 'paragraph',
            text: "Your journey from R1 to R2 is about turning scattered ideas into structured artifacts: a simple deck, a basic model, and a clear story. Once these are in place, you'll be seen not just as a dreamer, but as a founder beginning to build investor credibility.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are a Capital Dreamer.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: 'Your vision is alive, but your readiness has yet to be built. This is the most important — and exciting — moment in your journey.',
          },
          {
            kind: 'bullets',
            items: [
              'Download the Capital Readiness Kit — Lite and build your first 5-slide deck outline this week',
              'Use the Startup Budget Template to map your first-year cash needs',
              'Book a 15-min free consult with a Conncct Capital Advisor to review your first draft',
            ],
          },
          {
            kind: 'callout',
            text: 'Your target: progress to G1R2 — Pitch Explorer within the next 90 days.',
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R2',
    group: 'G1',
    rCode: 'R2',
    pillar: 'Capital Readiness',
    stage: 'Very High Risk Stage',
    tone: 'orange',
    profile: 'Pitch Explorer',
    tagline: "You've left the idea-only stage and entered the world of structured storytelling.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You have moved beyond pure vision. You've started shaping a pitch narrative and perhaps assembled an early deck or document. This is a strong signal of progress: you are exploring how to communicate your business to potential investors. However, at this stage, your fundraising assets remain incomplete, inconsistent, or not yet credible. If you attempted to pitch today, investors would likely be intrigued by the idea but unconvinced by the execution. Your current task is to transform draft materials into structured, investable assets. You are a Pitch Explorer — curious, motivated, and entering the fundraising world for the first time. Your next step is to channel this momentum into discipline.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'Your answers to the diagnostic highlight the following signals:' },
          {
            kind: 'bullets',
            items: [
              'Basic pitch slides exist — but they may be rough, text-heavy, or missing key sections.',
              'Financials are at a concept level — numbers may be aspirational or back-of-the-envelope.',
              'Storyline feels incomplete — it explains the idea but not the business case.',
              'Investor expectations not yet internalized — you may not be thinking in terms of traction, comparables, or returns.',
            ],
          },
          {
            kind: 'paragraph',
            text: "You've started the journey, but right now your pitch is not yet investor-grade.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Structure Your Pitch Deck Properly',
                sub: [
                  'Use a standard 10–12 slide sequence: Problem → Solution → Market → Business Model → Traction → Team → Ask',
                  'Focus on clarity, not design',
                  'Avoid jargon; make sure any outsider could grasp your business in five minutes.',
                ],
              },
              {
                title: 'Build a Draft Financial Model',
                sub: [
                  'Capture revenue drivers, key expenses, and basic funding needs',
                  'Keep it simple: 12-month forecast is enough at this stage',
                  'Focus on logic (how you make money) rather than perfection.',
                ],
              },
              {
                title: 'Practice Investor Thinking',
                sub: [
                  'Study 3–5 sample pitch decks from other startups',
                  "Compare your draft to theirs (what's missing? what stands out?)",
                  "Start learning the questions investors ask: What's your market size? Why now? Who else is doing this?",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, you'll get access to resources designed for exploration and iteration:",
          },
          {
            kind: 'bullets',
            items: [
              'Pitch Deck Starter Pack — 10-slide outline with examples',
              'Investor Expectations Guide — explains what angels, accelerators, and pre-seed VCs look for',
              'Draft Financial Model Template (Excel) — simple forecast structure to practice with numbers',
              'Deck Review Checklist — key criteria to assess whether your slides are pitch-ready',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: 'You are not ready to pitch investors yet, but you are building the muscle memory of storytelling and financial thinking. This is exactly where you should be: exploring, drafting, and iterating.',
          },
          {
            kind: 'paragraph',
            text: 'Think of yourself as a founder in "rehearsal mode." You wouldn\'t go on stage without practice; the same applies to fundraising. By working through templates, feedback loops, and practice decks, you\'ll soon be confident and credible.',
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are a Pitch Explorer.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've left the idea-only stage and entered the world of structured storytelling. Your challenge is to turn a rough draft into a compelling narrative.",
          },
          {
            kind: 'bullets',
            items: [
              'Download the Pitch Deck Starter Pack and build a 10-slide draft this week',
              'Use the Draft Financial Model Template to outline your basic revenue/expense logic',
              'Book a Capital Readiness Review Call to get expert feedback on your first deck',
            ],
          },
          {
            kind: 'callout',
            text: 'Your target: progress to G1R3 — Pitch Polisher within 90 days by strengthening and refining your draft assets.',
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R3',
    group: 'G1',
    rCode: 'R3',
    pillar: 'Capital Readiness',
    stage: 'High Risk Stage',
    tone: 'orange',
    profile: 'Pitch Polisher',
    tagline: 'You have the raw materials, but they must be sharpened to attract investor attention.',
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've made meaningful progress: you already have a draft pitch deck and possibly an early version of a financial model. You've translated your vision into something tangible — which sets you apart from many idea-stage founders. However, your materials are not yet sharp enough to convince investors. They may lack consistency, depth, or a compelling storyline. Right now, you are at the polishing stage: transforming rough drafts into professional-grade fundraising assets. You are a Pitch Polisher — you've moved past exploration and are now refining your materials to meet investor standards.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'From your responses, several clear signals stand out:' },
          {
            kind: 'bullets',
            items: [
              'Deck exists but lacks impact — slides may be too wordy, poorly designed, or missing traction/competitive data.',
              'Financial model is incomplete — often high-level, without assumptions, cash flow, or sensitivity testing.',
              'Investor story feels flat — you may explain "what" you do, but not convincingly answer "why now" or "why you."',
              "The \"ask\" is unclear — investors may not know how much you're raising or what milestones the money will unlock.",
            ],
          },
          {
            kind: 'paragraph',
            text: "Your work so far is valuable, but you're still two steps away from readiness.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Refine Your Pitch Deck',
                sub: [
                  'Strengthen Problem & Solution slides with data and insights',
                  'Add Traction Metrics (users, revenue, pilots, LOIs)',
                  'Clarify your competitive advantage — why you, why now',
                  'Rehearse your verbal pitch alongside the slides.',
                ],
              },
              {
                title: 'Upgrade Your Financial Model',
                sub: [
                  'Move from rough estimates to structured forecasts',
                  'Add cash flow, runway analysis, and basic valuation logic',
                  'Link assumptions clearly (e.g., users → revenue).',
                ],
              },
              {
                title: 'Define Investor Targeting',
                sub: [
                  'Build a shortlist of 15–20 potential investors (angels, seed funds, accelerators)',
                  'Map fit: stage focus, sector interest, ticket size',
                  'Organize in a simple CRM to track outreach.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At this stage, your platform unlocks professional-grade polishing tools:',
          },
          {
            kind: 'bullets',
            items: [
              'Capital Readiness Kit — Pro (deck polish guide, advanced valuation calculator)',
              'Pitch Wizard AI (basic edition) — generate cleaner, more professional slides',
              'Investor Targeting Template (Excel CRM) — structure your outreach pipeline',
              'Financial Model Enhancer — starter template for forecasts and cash flow',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You're no longer a dreamer or explorer — you're a builder of investor assets. But investors today see hundreds of decks each month; mediocrity will not cut it. Your challenge is to move from \"draft\" to \"compelling.\"",
          },
          {
            kind: 'paragraph',
            text: "Polish is not just design. It's about clarity of story, credibility of numbers, and strength of the ask. By tightening your materials now, you'll dramatically increase your chances of engaging serious investors when you move to R4/R5.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are a Pitch Polisher.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: 'You have the raw materials, but they must be sharpened to attract investor attention.',
          },
          {
            kind: 'bullets',
            items: [
              'Refine your deck using the Pitch Wizard AI (basic)',
              'Build a stronger financial model using the Pro Capital Readiness Kit',
              'Shortlist 15 target investors this month',
              'Schedule a Capital Diagnostic Review call to pressure-test your materials',
            ],
          },
          {
            kind: 'callout',
            text: 'Your next milestone: move to G1R4 — Funding Preparer, where your deck, numbers, and data room begin to look truly investor-grade.',
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R4',
    group: 'G1',
    rCode: 'R4',
    pillar: 'Capital Readiness',
    stage: 'Medium Risk Stage',
    tone: 'yellow',
    profile: 'Funding Preparer',
    tagline: "You've laid the foundation — now it's time to make your case irresistible.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: 'You\'ve made substantial progress: you now have a structured pitch deck, a basic financial model, and even an early data room starting to take shape. This places you well ahead of the early-stage curve — many founders never reach this point. However, while the essentials are there, they are not yet sharp, polished, or validated enough to convince investors. At this stage, you risk investors saying: "Interesting idea, but not quite there yet." You are a Funding Preparer — someone who has done the work to assemble materials but now needs to refine the story, strengthen the numbers, and package credibility.',
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'The diagnostic reveals clear indicators of your current stage:' },
          {
            kind: 'bullets',
            items: [
              'Deck is complete but lacks wow factor — storyline may be generic, traction light, or slides visually inconsistent.',
              'Financial model is present but assumptions are not benchmarked or defensible.',
              'Data room exists but is incomplete or scattered — contracts, cap table, or governance documents may be missing.',
              'Investor ask is defined but may not align with your stage (too much/too little capital, milestones unclear).',
            ],
          },
          {
            kind: 'paragraph',
            text: 'You\'ve crossed from "draft" to "structured," but not yet to "credible."',
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Sharpen Your Narrative',
                sub: [
                  'Strengthen competitive differentiation',
                  'Add traction data (growth rates, pilots, user adoption) to prove momentum',
                  'Ensure your "Ask" slide connects funding amount to specific milestones.',
                ],
              },
              {
                title: 'Validate Your Numbers',
                sub: [
                  'Benchmark your valuation and assumptions against comparable startups',
                  'Add sensitivity analysis (best/worst case)',
                  'Show runway logic: how long the raise sustains you and what milestones it unlocks.',
                ],
              },
              {
                title: 'Professionalize Your Data Room',
                sub: [
                  'Include incorporation docs, founder agreements, IP rights, cap table',
                  'Ensure consistency between your deck and your data room',
                  'Structure it in folders (Financials, Legal, Ops, Product).',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At this stage, your platform gives you advanced preparation tools:',
          },
          {
            kind: 'bullets',
            items: [
              'Pitch Wizard AI — Advanced Edition (storyline improvement + visual polish)',
              'Valuation Benchmark Guide (compare your numbers against industry peers)',
              'Data Room Checklist Pro (organized structure for fundraising documents)',
              'Investor Q&A Prep Workbook (anticipate tough investor questions)',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: 'You are no longer "early." You are approaching the line where investors will expect serious engagement. But investors have finely tuned filters — if your materials feel sloppy, inconsistent, or generic, they will quickly lose interest.',
          },
          {
            kind: 'paragraph',
            text: 'Your job now is to tighten credibility: make your pitch compelling, your numbers defensible, and your data room watertight. This is the difference between getting a meeting and closing a round.',
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are a Funding Preparer.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've laid the foundation — now it's time to make your case irresistible.",
          },
          {
            kind: 'bullets',
            items: [
              'Run your deck through Pitch Wizard AI — Advanced for refinement',
              'Use the Valuation Benchmark Guide to test your assumptions',
              'Organize your legal and financial docs with the Data Room Checklist Pro',
              'Book a Capital Readiness Simulation call to rehearse your pitch with an expert',
            ],
          },
          {
            kind: 'callout',
            text: 'Your next milestone: progress to G1R5 — Investor Prepped, where your deck, model, and data room will be strong enough to command investor confidence.',
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R5',
    group: 'G1',
    rCode: 'R5',
    pillar: 'Capital Readiness',
    stage: 'Semi-Ready Stage',
    tone: 'blue',
    profile: 'Investor Prepped',
    tagline: "You have the tools and structure in place — now it's about elevating performance.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: 'You\'ve done the heavy lifting: your deck is polished, your financial model is structured, and your data room is mostly complete. You\'re not just exploring or drafting anymore — you\'re nearly ready to face investors with confidence. At this stage, however, the devil is in the details. The difference between "almost ready" and "fully investor-ready" is your ability to defend assumptions, tailor your pitch to investor types, and handle tough questions under pressure. You are an Investor Prepped founder — well-structured and credible, but needing final adjustments to ensure your fundraising success.',
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'The diagnostic reveals your current status:' },
          {
            kind: 'bullets',
            items: [
              'Deck looks professional and follows a strong storyline, but may lack compelling differentiation or "wow factor."',
              'Financial model is robust, but you may not be fully prepared to defend assumptions or explain sensitivities.',
              'Data room is organized but could be missing advanced items (board minutes, compliance documents, IP assignments).',
              'Investor list is defined but your outreach plan and messaging are not yet optimized.',
            ],
          },
          {
            kind: 'paragraph',
            text: 'You are credible on paper, but investors will still test you.',
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Refine Investor Messaging',
                sub: [
                  'Tailor your pitch to different investor types (angels vs. VCs vs. accelerators)',
                  'Develop multiple versions of your deck (short teaser, full deck, appendix slides)',
                  'Align your "Ask" with investor interests and stage expectations.',
                ],
              },
              {
                title: 'Simulate Investor Q&A',
                sub: [
                  'Run mock pitch sessions to practice handling objections',
                  "Prepare concise, data-driven answers to common questions (How big is your market really? What's your CAC/LTV ratio? Why are you raising this amount, and why now?)",
                  'Create a FAQ document that can also be shared with investors.',
                ],
              },
              {
                title: 'Strengthen Valuation Defense',
                sub: [
                  'Benchmark your valuation against industry peers',
                  'Prepare 2–3 valuation defense angles (traction, comps, market growth)',
                  'Know your red lines on dilution and terms.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Your platform now unlocks tools for advanced rehearsal and precision:',
          },
          {
            kind: 'bullets',
            items: [
              'Investor FAQ Builder — anticipate and draft responses to tough questions',
              'Valuation Defense Toolkit — frameworks for benchmarking and explaining your valuation',
              'Outreach Playbook Template — design structured email sequences and investor communication plans',
              'Pitch Recording Guide — rehearse, record, and refine your delivery',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: 'You are on the verge of being fully investor-ready. Investors will take you seriously, but your ability to close funding depends on refinement, preparation, and confidence under scrutiny.',
          },
          {
            kind: 'paragraph',
            text: "This is not about making more slides — it's about mastering delivery, proving credibility, and showing composure. The final 10% of preparation can make the difference between a pass and a signed term sheet.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are Investor Prepped.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You have the tools and structure in place — now it's about elevating performance.",
          },
          {
            kind: 'bullets',
            items: [
              'Build your Investor FAQ this week and practice answers',
              'Use the Valuation Defense Toolkit to benchmark your numbers',
              'Develop a tiered outreach plan (warm intros first, then broader outreach)',
              'Schedule a Mock Investor Pitch Session with a Conncct consultant',
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: achieve G1R6 — Capital Ready, where you'll confidently engage investors with a compelling pitch, defensible numbers, and strong momentum.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R6',
    group: 'G1',
    rCode: 'R6',
    pillar: 'Capital Readiness',
    stage: 'Investor-Ready Stage',
    tone: 'green',
    profile: 'Capital Ready',
    tagline: "Your materials and preparation are in place. Now it's time to step onto the fundraising stage.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "Congratulations — you've reached the investor-ready threshold. Your pitch deck is sharp, your financial model is structured and defensible, and your data room is in place. At this stage, you are ready to start actively engaging investors. The risk level has shifted: your challenge is no longer credibility, but execution. You must now focus on outreach strategy, building investor momentum, and negotiating terms effectively. You are Capital Ready — investors will take your meeting seriously. The next 90 days will define your fundraising outcome.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'Your diagnostic results point to clear readiness indicators:' },
          {
            kind: 'bullets',
            items: [
              'Deck is professional, compelling, and complete — investors can easily grasp your story.',
              'Financial model is structured and defensible — your assumptions make sense and align with market benchmarks.',
              'Data room is organized — legal, financial, and operational documents are in place.',
              "Ask is clear and credible — investors can understand how much you're raising, how it will be used, and what milestones it unlocks.",
            ],
          },
          {
            kind: 'paragraph',
            text: 'In short: you check the boxes investors look for in a credible early-stage company.',
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Execute Outreach with Discipline',
                sub: [
                  'Build a structured investor CRM with 30–50 names',
                  'Prioritize warm introductions over cold emails',
                  'Launch outreach in waves, starting with lower-priority investors to refine delivery.',
                ],
              },
              {
                title: 'Create Momentum in Fundraising',
                sub: [
                  'Batch meetings close together to generate investor FOMO',
                  'Share consistent traction updates during the raise',
                  'Use early soft commitments to unlock credibility with larger funds.',
                ],
              },
              {
                title: 'Prepare for Negotiation & Due Diligence',
                sub: [
                  'Define your red lines on valuation, dilution, and board seats',
                  'Practice term sheet negotiation scenarios',
                  'Anticipate due diligence questions (legal, product, traction, financials).',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At this stage, your platform unlocks execution-focused resources:',
          },
          {
            kind: 'bullets',
            items: [
              'Investor CRM Template — organize, track, and prioritize outreach',
              'Negotiation Prep Guide — framework to prepare red lines, fallback positions, and deal strategies',
              'Deal Room Audit Checklist — ensure your documents are complete for due diligence',
              'Fundraising Timeline Planner — schedule outreach waves and target close dates',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: 'Being Capital Ready means investors will engage with you seriously. But readiness is not the same as success. The next challenge is building momentum and leverage. Investors are more likely to commit when they sense others are interested.',
          },
          {
            kind: 'paragraph',
            text: 'Execution discipline matters: scattershot outreach will dilute your chances, while structured, targeted outreach will build momentum and improve your odds of closing the round quickly.',
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are Capital Ready.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your materials and preparation are in place. Now it's time to step onto the fundraising stage.",
          },
          {
            kind: 'bullets',
            items: [
              'Populate your Investor CRM with at least 30 prospects',
              'Schedule intro calls in waves to build momentum',
              'Use the Negotiation Prep Guide to define your red lines',
              'Book a Fundraising Execution Session with a Conncct advisor to design your outreach strategy',
            ],
          },
          {
            kind: 'callout',
            text: 'Your next milestone: progress to G1R7 — Capital Magnet, where investors compete to join your round and you negotiate from a position of strength.',
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G1R7',
    group: 'G1',
    rCode: 'R7',
    pillar: 'Capital Readiness',
    stage: 'Exceptional Stage',
    tone: 'green',
    profile: 'Capital Magnet',
    tagline: 'Investors want in. You now have the leverage to choose wisely and negotiate firmly.',
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've achieved the pinnacle of capital readiness. Your pitch, numbers, and data room are not only investor-ready but investor-attractive. You're positioned to do more than raise money — you're positioned to choose your investors. At this stage, investors will not just take meetings — they will compete for a seat at your table. You have the leverage to optimize valuation, negotiate favorable terms, and attract strategic partners who will accelerate your company's growth beyond capital alone. You are a Capital Magnet — founders at this stage often secure better terms, higher valuations, and more influential investors.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: 'Your diagnostic responses reveal you have:' },
          {
            kind: 'bullets',
            items: [
              'A world-class pitch deck — compelling narrative, strong design, traction evidence, and clear differentiation.',
              'Robust financial model — detailed, benchmarked, sensitivity-tested, and defensible.',
              'Complete data room — audit-grade, organized, and ready for due diligence.',
              'Investor momentum already building — you may already have commitments or strong signals of interest.',
            ],
          },
          {
            kind: 'paragraph',
            text: "You've gone beyond readiness to excellence.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Maximize Leverage with Investors',
                sub: [
                  'Run a tight fundraising process (batch meetings, short timelines, strong updates)',
                  'Use early commitments to trigger FOMO among larger funds',
                  'Be deliberate: choose investors who align strategically, not just financially.',
                ],
              },
              {
                title: 'Negotiate from Strength',
                sub: [
                  'Optimize valuation while protecting long-term ownership',
                  'Push for favorable terms (board structure, liquidation preferences, founder protections)',
                  'Secure not just money, but value-added investors (network, talent, partnerships).',
                ],
              },
              {
                title: 'Position for Scaling Beyond Capital',
                sub: [
                  'Align fundraising with international expansion, strategic partnerships, or major hires',
                  'Start preparing for Series A/B expectations (governance, compliance, board readiness)',
                  'Document your growth story for future investors.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: 'At this elite stage, your platform unlocks advanced scaling and negotiation resources:',
          },
          {
            kind: 'bullets',
            items: [
              'Strategic Partner Gateway Guide — identify and approach investors who bring more than money',
              'Advanced Term Sheet Negotiation Toolkit — scenarios, benchmarks, and protections',
              'Scaling Governance Checklist — ensure governance, ESG, and board practices are Series A+ ready',
              'Global Fundraising Playbook — guidance for cross-border investor relations',
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You're in rare territory: founders who reach this level are not only raising successfully, they're shaping long-term investor relationships that can define the company's future.",
          },
          {
            kind: 'paragraph',
            text: 'Your biggest risk now is not failure, but choosing the wrong investors or over-optimizing valuation at the expense of strategic fit. Remember: smart money accelerates; dumb money complicates.',
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: 'You are a Capital Magnet.', tone: 'blue' },
          {
            kind: 'paragraph',
            text: 'Investors want in. You now have the leverage to choose wisely and negotiate firmly.',
          },
          {
            kind: 'bullets',
            items: [
              'Run a tight, time-bound raise to maximize momentum',
              'Use the Advanced Term Sheet Toolkit to defend founder interests',
              'Prioritize strategic partners who bring networks, clients, or expertise',
              'Schedule a Strategic Fundraising Session with a Conncct advisor to design your investor selection strategy',
            ],
          },
          {
            kind: 'callout',
            text: 'Your next frontier: scaling internationally, raising from top-tier funds, and positioning for Series A/B excellence.',
            tone: 'green',
          },
        ],
      },
    ],
  },
];
